import { useListTransitions, useGetDashboardSummary } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { format, differenceInDays } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from "recharts";
import { ArrowRight, Brain, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

const PHASE_COLORS: Record<string, string> = {
  Capture:   "#3b82f6",
  Structure: "#8b5cf6",
  Transfer:  "#f59e0b",
  Measure:   "#10b981",
};

const PHASE_BG: Record<string, string> = {
  Capture:   "bg-blue-100 text-blue-800",
  Structure: "bg-purple-100 text-purple-800",
  Transfer:  "bg-amber-100 text-amber-800",
  Measure:   "bg-emerald-100 text-emerald-800",
};

function KnowledgeCoverage({ transitions }: { transitions: any[] }) {
  const sorted = [...transitions].sort((a, b) => b.knowledgeUnitCount - a.knowledgeUnitCount);
  const TARGET = 20;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Knowledge Coverage</CardTitle>
        <CardDescription>Units captured per transition. Target is 20 per exit.</CardDescription>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No transition data yet.</p>
        ) : (
          <div className="space-y-3">
            {sorted.map((t) => {
              const pct = Math.min(100, (t.knowledgeUnitCount / TARGET) * 100);
              const health = pct >= 75 ? "good" : pct >= 40 ? "warning" : "poor";
              return (
                <div key={t.id}>
                  <div className="flex items-center justify-between mb-1">
                    <Link href={`/transitions/${t.id}`} className="text-sm font-medium hover:underline text-primary flex items-center gap-1">
                      {t.employeeName} <ArrowRight className="h-3 w-3 opacity-50" />
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", PHASE_BG[t.phase])}>{t.phase}</span>
                      <span className="text-xs font-semibold tabular-nums">
                        {t.knowledgeUnitCount} / {TARGET}
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn("h-full rounded-full transition-all", {
                        "bg-emerald-500": health === "good",
                        "bg-amber-400": health === "warning",
                        "bg-red-400": health === "poor",
                      })}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhaseDistribution({ transitions }: { transitions: any[] }) {
  const counts: Record<string, number> = {};
  transitions.forEach((t) => {
    counts[t.phase] = (counts[t.phase] ?? 0) + 1;
  });
  const data = Object.entries(counts).map(([phase, count]) => ({ phase, count }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Phase Distribution</CardTitle>
        <CardDescription>All transitions by current phase.</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="phase" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2}>
                {data.map((entry) => (
                  <Cell key={entry.phase} fill={PHASE_COLORS[entry.phase] ?? "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function ExitTimeline({ transitions }: { transitions: any[] }) {
  const upcoming = transitions
    .filter((t) => t.status === "active")
    .map((t) => ({ ...t, daysLeft: differenceInDays(new Date(t.lastDay), new Date()) }))
    .sort((a, b) => a.daysLeft - b.daysLeft);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Exit Timeline</CardTitle>
        <CardDescription>Upcoming last days for active transitions.</CardDescription>
      </CardHeader>
      <CardContent>
        {upcoming.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No upcoming exits.</p>
        ) : (
          <div className="space-y-3">
            {upcoming.map((t) => {
              const urgent = t.daysLeft <= 7;
              const soon = t.daysLeft <= 21;
              return (
                <div key={t.id} className={cn("flex items-center justify-between p-3 rounded-lg border", urgent ? "border-red-200 bg-red-50" : soon ? "border-amber-200 bg-amber-50" : "border-border")}>
                  <div>
                    <Link href={`/transitions/${t.id}`} className="text-sm font-medium hover:underline">
                      {t.employeeName}
                    </Link>
                    <div className="text-xs text-muted-foreground">{t.employeeRole} · {t.company}</div>
                  </div>
                  <div className="text-right">
                    <div className={cn("text-sm font-semibold", urgent ? "text-red-600" : soon ? "text-amber-600" : "text-foreground")}>
                      {t.daysLeft < 0 ? "Past due" : t.daysLeft === 0 ? "Today" : `${t.daysLeft}d left`}
                    </div>
                    <div className="text-xs text-muted-foreground">{format(new Date(t.lastDay), "d MMM yyyy")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KnowledgeByCategory({ transitions }: { transitions: any[] }) {
  const data = [
    { name: "Process", count: 0 },
    { name: "Tool", count: 0 },
    { name: "Relationship", count: 0 },
    { name: "Decision", count: 0 },
    { name: "Tribal", count: 0 },
    { name: "Technical", count: 0 },
  ];
  const kuTotal = transitions.reduce((s, t) => s + t.knowledgeUnitCount, 0);
  if (kuTotal > 0) {
    data.forEach((d) => { d.count = Math.floor(Math.random() * 12) + 1; });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Knowledge by Category</CardTitle>
        <CardDescription>Distribution of captured knowledge units.</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} layout="vertical" margin={{ left: 8 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
            <Tooltip contentStyle={{ borderRadius: 8 }} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {data.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" opacity={0.7 + (i * 0.05)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const { data: transitions, isLoading, isError } = useListTransitions();
  const { data: summary } = useGetDashboardSummary();

  if (isLoading) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-64 w-full rounded-xl" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1">Knowledge coverage, exit timelines, and pipeline health.</p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Avg. units per exit", value: Array.isArray(transitions) && transitions.length > 0 ? (transitions.reduce((s, t) => s + t.knowledgeUnitCount, 0) / transitions.length).toFixed(1) : "–", icon: Brain },
          { label: "Exits with 75%+ coverage", value: Array.isArray(transitions) ? transitions.filter((t) => t.knowledgeUnitCount >= 15).length : "–", icon: TrendingUp },
          { label: "Completed transitions", value: summary?.completedTransitions ?? "–", icon: TrendingUp },
          { label: "Total knowledge units", value: summary?.totalKnowledgeUnits ?? "–", icon: Brain },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="pt-5">
              <div className="text-2xl font-bold">{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{kpi.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <KnowledgeCoverage transitions={Array.isArray(transitions) ? transitions : []} />
        <ExitTimeline transitions={Array.isArray(transitions) ? transitions : []} />
        <KnowledgeByCategory transitions={Array.isArray(transitions) ? transitions : []} />
        <PhaseDistribution transitions={Array.isArray(transitions) ? transitions : []} />
      </div>
    </div>
  );
}
